import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Settings as SettingsIcon,
  Subscriptions as SubscriptionsIcon,
  Podcasts as PodcastsIcon,
  TrendingUp as TrendingUpIcon,
  Book as VocabularyIcon,
  Assessment as ReportsIcon,
  MenuBook as JournalIcon,
  Article as ArticleIcon,
  ExpandLess,
  ExpandMore,
  Science as ExperimentsIcon,
  AutoStories as StoryTimeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, type ReactElement } from 'react';
import { ROUTES } from '../../config/constants';
import { useVocabularyStats } from '../../hooks/useVocabulary';

const drawerWidth = 240;

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

export const Sidebar = ({ mobileOpen, onDrawerToggle }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');

  const [experimentsOpen, setExperimentsOpen] = useState(() =>
    location.pathname.startsWith(ROUTES.STORYTIME)
  );

  useEffect(() => {
    if (location.pathname.startsWith(ROUTES.STORYTIME)) {
      setExperimentsOpen(true);
    }
  }, [location.pathname]);

  // Fetch vocabulary stats to get due count
  const { data: vocabularyStats } = useVocabularyStats();
  const dueCount = vocabularyStats?.dueForReview || 0;

  const menuItems: Array<{
    text: string;
    icon: ReactElement;
    path: string;
    /** Optional anchor id for onboarding tours (rendered as data-tour). */
    tourId?: string;
  }> = [
    {
      text: t('dashboard'),
      icon: <DashboardIcon />,
      path: ROUTES.DASHBOARD,
    },
    {
      text: t('projects'),
      icon: <FolderIcon />,
      path: ROUTES.PROJECTS,
    },
    {
      text: t('searchPodcasts'),
      icon: <PodcastsIcon />,
      path: '/podcasts/search',
      tourId: 'nav-search-podcasts',
    },
    {
      text: t('popularPodcasts'),
      icon: <TrendingUpIcon />,
      path: '/podcasts/popular',
      tourId: 'nav-popular-podcasts',
    },
    {
      text: t('vocabulary'),
      icon: <VocabularyIcon />,
      path: ROUTES.VOCABULARY,
    },
    {
      text: t('journals'),
      icon: <JournalIcon />,
      path: ROUTES.JOURNALS,
    },
    {
      text: t('reports'),
      icon: <ReportsIcon />,
      path: ROUTES.REPORTS,
    },
  ];

  const bottomMenuItems = [
    {
      text: t('subscription'),
      icon: <SubscriptionsIcon />,
      path: ROUTES.SUBSCRIPTION,
    },
    {
      text: t('settings'),
      icon: <SettingsIcon />,
      path: ROUTES.SETTINGS,
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  const isActive = (path: string) => {
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isStoryTimeActive = isActive(ROUTES.STORYTIME);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar /> {/* Spacer for fixed AppBar */}
      
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => {
          // Check if this is the vocabulary menu item to add count
          const isVocabulary = item.path === ROUTES.VOCABULARY;
          const showCount = isVocabulary && dueCount > 0;
          const displayText = showCount ? `${item.text} (${dueCount})` : item.text;
          
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActive(item.path)}
                onClick={() => handleNavigation(item.path)}
                {...(item.tourId ? { 'data-tour': item.tourId } : undefined)}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.main',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? 'primary.contrastText' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={displayText} />
              </ListItemButton>
            </ListItem>
          );
        })}

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setExperimentsOpen((prev) => !prev)}
            aria-expanded={experimentsOpen}
            aria-controls="experiments-nav-list"
            data-tour="nav-experiments"
          >
            <ListItemIcon>
              <ExperimentsIcon />
            </ListItemIcon>
            <ListItemText primary={t('experiments')} />
            {experimentsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={experimentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding id="experiments-nav-list">
            <ListItem disablePadding>
              <ListItemButton
                selected={isStoryTimeActive}
                onClick={() => handleNavigation(ROUTES.STORYTIME)}
                sx={{
                  pl: 4,
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.main' },
                    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                  },
                }}
              >
                <ListItemIcon sx={{ color: isStoryTimeActive ? 'primary.contrastText' : 'inherit' }}>
                  <StoryTimeIcon />
                </ListItemIcon>
                <ListItemText primary={t('storyTime')} />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>
      </List>

      <Divider />

      <List>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href="https://blog.scribelate.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <ArticleIcon />
            </ListItemIcon>
            <ListItemText primary={t('blog')} />
          </ListItemButton>
        </ListItem>
        {bottomMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive(item.path) ? 'primary.contrastText' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="navigation menu"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
